"use client";

import Link from "next/link";
import Image from "next/image";
import { m } from "framer-motion";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";
import { PROJECTS, accentColors } from "@/data/projects";

const Portfolio = () => {
  return (
    <section id="work" className="relative px-4 py-24">
      {/* Section divider */}
      <div className="section-divider mx-auto mb-20 max-w-xl" />

      <div className="mx-auto max-w-6xl">
        {/* Section header */}
        <SectionHeader
          index="02"
          code="work"
          align="right"
          title={<>Some things I&apos;ve <span className="gradient-text">built</span></>}
          blurb="Some are client work. Some are side projects. All are mine."
        />

        {/* Project cards */}
        <div className="grid gap-8 md:grid-cols-2">
          {PROJECTS.map((project, index) => {
            const colors = accentColors[project.accent];
            return (
              <AnimatedSection key={project.name} delay={index * 0.05}>
                <m.div
                  whileHover={{ y: -6 }}
                  className={`group relative block overflow-hidden rounded-2xl bg-white/[0.02] p-8 ring-1 ring-white/[0.06] transition-all ${colors.ring}`}
                >
                    {/* Background gradient on hover */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${project.gradient} opacity-0 transition-opacity group-hover:opacity-100`}
                    />

                    <div className="relative z-10">
                      {/* Project image */}
                      {project.image && (
                        <div className="mb-5 flex items-center justify-center rounded-xl bg-white/[0.03] p-4 ring-1 ring-white/[0.06]">
                          <Image
                            src={project.image}
                            alt={`${project.name} logo`}
                            width={200}
                            height={100}
                            // Rendered at h-16 (64px tall, ~128px box) — the
                            // old 90vw/40vw made Next ship a 640px file for a
                            // 64px logo. Pin to the real slot so it serves a
                            // 128px candidate (2× DPR) instead.
                            sizes="128px"
                            className="h-16 w-auto object-contain"
                          />
                        </div>
                      )}

                      <div className="mb-4 flex items-center justify-between">
                        <h3 className="font-[family-name:var(--font-space-grotesk)] text-xl font-bold text-white">
                          <Link
                            href={`/work/${project.slug}`}
                            className="before:absolute before:inset-0 before:z-0 before:content-['']"
                            aria-label={`View ${project.name} details`}
                          >
                            {project.name}
                          </Link>
                        </h3>
                        {project.status === "live" ? (
                          <span className="flex items-center gap-1.5 text-xs text-emerald-400">
                            <span className="relative flex h-2 w-2">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                            </span>
                            Live
                          </span>
                        ) : project.status === "private" ? (
                          <span className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs text-[#94A3B8]">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            Private
                          </span>
                        ) : (
                          <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-[#94A3B8]">
                            Coming Soon
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      <p className="mb-6 text-sm leading-relaxed text-[#94A3B8]">
                        {project.description}
                      </p>

                      {/* Tech badges */}
                      <div className="flex flex-wrap gap-2">
                        {project.tech.map((t) => (
                          <span
                            key={t}
                            className={`rounded-full px-3 py-1 text-xs ring-1 ${colors.badge}`}
                          >
                            {t}
                          </span>
                        ))}
                      </div>

                      {/* Action links */}
                      <div className="mt-6 flex items-center gap-4 opacity-0 transition-opacity group-hover:opacity-100">
                        <div className={`flex items-center gap-2 text-sm font-medium ${colors.text}`}>
                          View Details
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        {project.url && (
                          <a
                            href={project.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={`relative z-10 flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs font-medium ${colors.text} ring-1 ring-white/10 transition-colors hover:bg-white/10`}
                          >
                            Visit Site
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                              <path d="M6 3H3V13H13V10M9 3H13V7M13 3L7 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </a>
                        )}
                        {project.slug === "bidflow" && (
                          <Link
                            href="/anatomy"
                            onClick={(e) => e.stopPropagation()}
                            className={`relative z-10 flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs font-medium ${colors.text} ring-1 ring-white/10 transition-colors hover:bg-white/10`}
                          >
                            Anatomy
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                              <path d="M8 3v10M8 13l-3-3M8 13l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </Link>
                        )}
                      </div>
                    </div>
                </m.div>
              </AnimatedSection>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
